import ko from 'knockout';
import { DateTime } from 'luxon';

import localization from '@/global/localization';
import dateformat_to_luxon from '@/util/dateformat_to_luxon';

declare module 'luxon' {
    // eslint-disable-next-line typescript/no-shadow
    interface DateTime {
        asLocaleDate(): string;
        asLocaleTime(): string;
        asLocaleDateTime(): string;
    }
    namespace DateTime {
        function fromDate(date: string): DateTime;
        function fromTime(time: string): DateTime;
        function fromDateTime(dateTime: string): DateTime;
        function asLocaleDate(dateTime: string | undefined): string;
        function asLocaleTime(dateTime: string | undefined): string;
        function asLocaleDateTime(dateTime: string | undefined): string;
    }
}

const dateformat = ko.pureComputed(() => {
    const loc = localization();
    return loc && dateformat_to_luxon(loc.dateformat);
});

DateTime.prototype.asLocaleDate = function () {
    const format = dateformat();
    if (format) {
        return this.toFormat(format);
    }
    return this.toLocaleString(DateTime.DATE_SHORT);
};

DateTime.prototype.asLocaleTime = function () {
    return this.toLocaleString(DateTime.TIME_SIMPLE);
};

DateTime.prototype.asLocaleDateTime = function () {
    if (dateformat()) {
        return this.toFormat(`${dateformat()} tt`);
    }
    return this.toLocaleString(DateTime.DATETIME_SHORT_WITH_SECONDS);
};

DateTime.fromDate = function (date: string): DateTime {
    return DateTime.fromFormat(date, 'yyyy-MM-dd');
};

DateTime.fromTime = function (time: string): DateTime {
    const timeWithSeconds = DateTime.fromFormat(time, 'HH:mm:ss');
    if (timeWithSeconds.isValid) {
        return timeWithSeconds;
    }
    return DateTime.fromFormat(time, 'HH:mm');
};

DateTime.fromDateTime = function (dateTime: string): DateTime {
    const isoDateTime = DateTime.fromISO(dateTime);
    if (isoDateTime.isValid) {
        return isoDateTime;
    }
    return DateTime.fromSQL(dateTime);
};

function makeDateTime(dateTime: string | undefined): DateTime | string {
    if (!dateTime) {
        return '';
    }

    let dt = DateTime.fromSQL(dateTime);
    if (dt.isValid) {
        return dt;
    }

    dt = DateTime.fromISO(dateTime);
    if (dt.isValid) {
        return dt;
    }

    dt = DateTime.fromRFC2822(dateTime);
    if (dt.isValid) {
        return dt;
    }

    dt = DateTime.fromJSDate(new Date(dateTime));
    if (dt.isValid) {
        return dt;
    }

    if (typeof dateTime === 'string') {
        return dateTime;
    }

    return 'Invalid Date'; // Should rarely happen.
}

DateTime.asLocaleDate = function (dateTime: string | undefined): string {
    const dt = makeDateTime(dateTime);
    return typeof dt === 'string' ? dt : dt.asLocaleDate();
};
DateTime.asLocaleTime = function (dateTime: string | undefined): string {
    const dt = makeDateTime(dateTime);
    return typeof dt === 'string' ? dt : dt.asLocaleTime();
};

DateTime.asLocaleDateTime = function (dateTime: string | undefined): string {
    const dt = makeDateTime(dateTime);
    return typeof dt === 'string' ? dt : dt.asLocaleDateTime();
};
